// @ts-strict-ignore
import type { Services } from '~/flow/types';

import type {
  Action,
  LegacyThreshold,
  ObjectIdTransformationMap,
  ReasonCodeGroup,
  Threshold,
  LegacyAggregationComponent,
} from '../../types';
import { getAttributeToken } from '../../utils';
import { generatePublicId } from '../../../../service/publicIds';
import { ActionType, WorkflowTransformationIssueType } from '../../constants';
import type { AggregationFilterOperatorType, AggregationType } from '../../constants';
import { isActionOfType } from 'flow/service/actions';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';

export function transformMLAAttributeComponents({ attributeFilterComponents }) {
  return attributeFilterComponents.map(filterComp => {
    const component = filterComp.attribute_component || filterComp;
    return {
      value: component.is_mock_mla_filter ? undefined : filterComp.value,
      attributeComponent: {
        attributeComponentId: component.attribute_component_id,
        precedence: component.precedence,
        placeholder: component.placeholder,
        dataType: component.data_type,
        label: component.label,
        isOptional: component.is_optional,
        possibleValues: component.possible_values,
        default: component.default,
      },
    };
  });
}

export function transformLegacyThresholdsToJsonRepresentation({
  thresholds,
  transformedActions,
  services,
  legacyActionsToTransformedActionsIdMap,
  reasonCodeGroups,
}: {
  thresholds: LegacyThreshold[];
  transformedActions: Record<string, Action>;
  services: Services;
  legacyActionsToTransformedActionsIdMap: ObjectIdTransformationMap;
  reasonCodeGroups: ReasonCodeGroup[];
}) {
  const transformedJsonThresholds = {};
  const issuesWithTransformedJsonThresholds = [];
  const legacyToTransformedThresholdIdMap: Record<string, string> = {};

  thresholds.forEach(threshold => {
    const objectPublicId = threshold.publicId
      ? threshold.publicId : generatePublicId('threshold');
    const leftActionId = legacyActionsToTransformedActionsIdMap[threshold.leftActionId];
    const rightActionId = threshold.rightActionId
      ? legacyActionsToTransformedActionsIdMap[threshold.rightActionId]
      : null;
    const leftAction = transformedActions[leftActionId];
    const rightAction = rightActionId
      ? transformedActions[rightActionId]
      : null;

    const owningActionPublicId = legacyActionsToTransformedActionsIdMap[threshold.actionId];

    const rightReasonCodeGroupId = threshold.rightCRGId;
    let matchingReasonCodeGroup: ReasonCodeGroup | null = null;
    if (rightReasonCodeGroupId) {
      matchingReasonCodeGroup = reasonCodeGroups.find(
        reasonCodeGroup => reasonCodeGroup.id === rightReasonCodeGroupId,
      );
    }
    const transformedThresholdObject: Threshold = {
      publicId: objectPublicId,
      actionId: owningActionPublicId,
      left: {
        actionId: leftActionId,
        attributeId: threshold.leftAttrId,
        attributeToken: getAttributeToken({
          action: leftAction,
          attributeId: threshold.leftAttrId,
          services,
        }),
        attributeComponents: transformMLAAttributeComponents({
          attributeFilterComponents: threshold.leftAttrComponents,
        }),
        aggregationComponents: transformAggregationComponents({
          aggregationComponents: (() => {
            // this is temporary to track down an issue where some thresholds have undefined left aggregationComponents -- OAK-1150
            const aggComp = threshold.leftAggregationComponents;
            if (!aggComp) {
              captureExceptionsToSentry(
                new Error('[thresholds] leftAggregationComponents undefined'),
                { thresholdId: threshold.id },
              );
            }
            return aggComp ?? [];
          })(),
        }),
      },
      operator: threshold.operator,
      right: {
        actionId: rightActionId,
        attributeId: threshold.rightAttrId,
        attributeToken: getAttributeToken({
          action: rightAction,
          attributeId: threshold.rightAttrId,
          services,
        }),
        attributeComponents: transformMLAAttributeComponents({
          attributeFilterComponents: threshold.rightAttrComponents,
        }),
        reasonCodeGroupId: threshold.rightCRGId,
        reasonCodeGroup: matchingReasonCodeGroup || null,
        attributeValue: threshold.rightAttrValue,
        leftTagActionResult: threshold.rightTagActionSet,
        aggregationComponents: transformAggregationComponents({
          aggregationComponents: (() => {
            // this is temporary to track down an issue where some thresholds have undefined right aggregationComponents -- OAK-1150
            const aggComp = threshold.rightAggregationComponents;
            if (!aggComp) {
              captureExceptionsToSentry(
                new Error('[thresholds] rightAggregationComponents undefined'),
                { thresholdId: threshold.id },
              );
            }
            return aggComp ?? [];
          })(),
        }),
      },
    };

    // Validate that matrix model actions are not used as right-hand side actions
    // This is a core business rule validation that should always run
    if (rightAction && isActionOfType(rightAction, [
      ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
    ])) {
      issuesWithTransformedJsonThresholds.push({
        publicId: objectPublicId,
        manualResolution: false,
        type: WorkflowTransformationIssueType.MATRIX_MODEL_NOT_SUPPORTED_AS_RHS_ACTION,
        data: transformedThresholdObject,
      });
    }

    transformedJsonThresholds[objectPublicId] = transformedThresholdObject;
    legacyToTransformedThresholdIdMap[threshold.actId] = objectPublicId;
  });

  return {
    transformedThresholds: transformedJsonThresholds,
    legacyThresholdsTransformationMap: legacyToTransformedThresholdIdMap,
    issuesWithTransformedJsonThresholds,
  };
}

export function transformAggregationComponents({
  aggregationComponents,
}:
{
  aggregationComponents: LegacyAggregationComponent[];
}) {
  return aggregationComponents.map(aggComp => ({
    objectSlug: aggComp.object_slug,
    objectVersionPublicVersion: aggComp.object_version_public_version,
    displayName: aggComp.display_name,
    displayDescription: aggComp.display_description,
    aggregation: {
      columnSlug: aggComp.aggregation.column_slug,
      aggregationType: aggComp.aggregation.agg_type as AggregationType,
      ...(aggComp.aggregation.published_attribute_metadata && {
        publishedAttributeMetadata: {
          publishedAttributeToken:
            aggComp.aggregation.published_attribute_metadata
              .published_attribute_token,
          publishedAttributeDescription:
            aggComp.aggregation.published_attribute_metadata
              .published_attribute_description ?? null,
          publishedAttributeName:
            aggComp.aggregation.published_attribute_metadata
              .published_attribute_name ?? null,
          publishedAttributeDataType:
            aggComp.aggregation.published_attribute_metadata
              .published_attribute_data_type ?? null,
          containsPii:
            aggComp.aggregation.published_attribute_metadata
              .contains_pii ?? false,
        },
      }),
    },
    filters: (aggComp.filters || []).map(filter => ({
      columnSlug: filter.column_slug,
      operatorSlug: filter.operator_slug as AggregationFilterOperatorType,
      staticValue: filter.static_value,
      dynamicColumnSlug: filter.dynamic_column_slug ?? null,
      ...(filter.published_attribute_metadata && {
        publishedAttributeMetadata: {
          publishedAttributeToken:
            filter.published_attribute_metadata.published_attribute_token,
          publishedAttributeDescription:
            filter.published_attribute_metadata.published_attribute_description ?? null,
          publishedAttributeName:
            filter.published_attribute_metadata.published_attribute_name ?? null,
          publishedAttributeDataType:
            filter.published_attribute_metadata.published_attribute_data_type ?? null,
          containsPii:
            filter.published_attribute_metadata.contains_pii ?? false,
        },
      }),
    })),
  }));
}
